import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FieldSelect from './FieldSelect';
import OperatorSelect from './OperatorSelect';
import ValueInput from './ValueInput';
import { useRulesetResourceChoices } from '../hooks/useRulesetResourceChoices';
import {
  ConditionValue,
  IConditionLeaf,
  IRulesetSchemaField,
} from '../../../types/ruleset-types';

interface ConditionLeafProps {
  condition: IConditionLeaf;
  schema: { fields: IRulesetSchemaField[] };
  onChange: (condition: IConditionLeaf) => void;
  onRemove: () => void;
  dataPoolId: string;
  disabled?: boolean;
}

// Helper to get the field identifier (API uses 'field_path' as the identifier)
const getFieldId = (f: IRulesetSchemaField): string => {
  return (f as any).field_path ?? f.name ?? (f as any).key ?? (f as any).id ?? (f as any).field ?? '';
};

const getSelectedField = (
  fields: IRulesetSchemaField[],
  fieldValue: string
): IRulesetSchemaField | undefined => {
  return fields.find((field) => {
    const fieldId = getFieldId(field);
    if (!fieldId) {
      return false;
    }
    if (fieldId === fieldValue) {
      return true;
    }
    return !!field.dynamic && fieldValue.startsWith(`${fieldId}.`);
  });
};

const ConditionLeaf: React.FC<ConditionLeafProps> = ({
  condition,
  schema,
  onChange,
  onRemove,
  dataPoolId,
  disabled = false,
}) => {
  // Ensure schema.fields is an array
  const fields = Array.isArray(schema?.fields) ? schema.fields : [];

  // Normalize the field value (treat undefined as empty string)
  const fieldValue = condition.field ?? '';
  const operatorValue = condition.operator ?? '';

  const selectedField = getSelectedField(fields, fieldValue);
  const selectedFieldId = selectedField ? getFieldId(selectedField) : '';
  const dynamicKey =
    selectedField?.dynamic && fieldValue.startsWith(`${selectedFieldId}.`)
      ? fieldValue.slice(selectedFieldId.length + 1)
      : '';
  const operators = Array.isArray(selectedField?.operators) ? selectedField.operators : [];
  const selectedOperator = operators.find((op) => op.name === operatorValue) || null;
  const hasModifiers = selectedOperator?.modifiers?.includes('case_insensitive');
  const resourceChoices = useRulesetResourceChoices({
    source: selectedField?.choices_source,
    dataPoolId,
    enabled: !!selectedField?.choices_source,
  });
  const valueChoices =
    selectedField?.choices && selectedField.choices.length > 0
      ? selectedField.choices
      : resourceChoices;

  const handleFieldChange = (field: string) => {
    const newField = fields.find((f) => getFieldId(f) === field);
    const newOperators = Array.isArray(newField?.operators) ? newField.operators : [];
    const defaultOperator = newOperators[0]?.name || '';
    const nextFieldValue = newField?.dynamic ? field : field || '';
    onChange({
      ...condition,
      field: nextFieldValue,
      operator: defaultOperator,
      value: null,
      modifiers: undefined,
    });
  };

  const handleOperatorChange = (operator: string) => {
    const op = operators.find((o) => o.name === operator);
    // Reset value when operator changes to a type that doesn't need value
    const newValue = op?.value_type === 'none' ? null : condition.value;
    onChange({
      ...condition,
      operator,
      value: newValue,
    });
  };

  const handleValueChange = (value: ConditionValue) => {
    onChange({
      ...condition,
      value,
    });
  };

  const handleCaseInsensitiveChange = (checked: boolean) => {
    onChange({
      ...condition,
      modifiers: checked ? { case_insensitive: true } : undefined,
    });
  };

  const handleDynamicKeyChange = (key: string) => {
    if (!selectedFieldId) {
      return;
    }

    const trimmedKey = key.trim();
    onChange({
      ...condition,
      field: trimmedKey ? `${selectedFieldId}.${trimmedKey}` : selectedFieldId,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 1.5,
        py: 1.5,
        px: 2,
        backgroundColor: '#fff',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 0,
      }}
      data-cy="condition-leaf"
    >
      <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
        <FieldSelect
          value={fieldValue}
          fields={fields}
          onChange={handleFieldChange}
          disabled={disabled}
        />
      </Box>

      {selectedField?.dynamic && (
        <TextField
          size="small"
          variant="outlined"
          placeholder="Enrichment key"
          value={dynamicKey}
          onChange={(e) => handleDynamicKeyChange(e.target.value)}
          disabled={disabled}
          sx={{ flex: '1 1 180px', minWidth: 0 }}
          inputProps={{ style: { fontSize: '13px' } }}
          data-cy="input-condition-dynamic-key"
        />
      )}

      <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
        <OperatorSelect
          value={operatorValue}
          operators={operators}
          onChange={handleOperatorChange}
          disabled={disabled || !fieldValue}
        />
      </Box>

      <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
        <ValueInput
          value={condition.value}
          operator={selectedOperator}
          choices={valueChoices}
          fieldType={selectedField?.type}
          onChange={handleValueChange}
          disabled={disabled || !operatorValue}
        />
      </Box>

      {hasModifiers && (
        <FormControlLabel
          control={
            <Checkbox
              checked={condition.modifiers?.case_insensitive || false}
              onChange={(e) => handleCaseInsensitiveChange(e.target.checked)}
              disabled={disabled}
              size="small"
              data-cy="checkbox-case-insensitive"
            />
          }
          label="Case insensitive"
          sx={{
            ml: 0,
            mr: 0,
            alignSelf: 'center',
            flex: '0 1 132px',
            '& .MuiFormControlLabel-label': { fontSize: '12px', color: 'text.secondary' },
          }}
        />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }} />

      <Tooltip title="Remove condition">
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={disabled}
          sx={{
            alignSelf: 'center',
            color: 'text.disabled',
            '&:hover': {
              color: 'error.main',
              backgroundColor: 'error.lighter',
            },
          }}
          data-cy="btn-remove-condition"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ConditionLeaf;
